import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { NAV } from "@/constants/testIds";
import { NotificationsBell } from "@/components/NotificationsBell";
import { GamificationBadge } from "@/components/altiora/GamificationBadge";
import { FounderProfileSwitcher } from "@/components/layout/FounderProfileSwitcher";
import { ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";
import { CommandPaletteTrigger } from "@/components/altiora/CommandPalette";
import { MobileNav } from "@/components/layout/MobileNav";
import {
  NAVY, NAVY_DARK, CHAMPAGNE, GOLD, IVORY, SLATE,
} from "@/components/altiora/PremiumUI";

/**
 * ── LangSwitcher — Landing-Grade dark navy variant ──────────────────
 * Champagne-highlighted active state, ivory typography, sits comfortably
 * on the navy TopBar. Fully accessible (keyboard-focus visible via global CSS).
 */
export function LangSwitcher({ testId = NAV.langSwitcher }) {
  const { lang, setLang } = useI18n();
  const langs = ["fr", "en"];
  return (
    <div className="inline-flex items-center rounded-md overflow-hidden text-[11px] font-mono"
             style={{ border: `1px solid ${CHAMPAGNE}44` }}
             data-testid={testId}>
      {langs.map((code) => {
        const on = lang === code;
        return (
          <button
            key={code}
            onClick={() => setLang(code)}
            data-testid={`${testId}-${code}`}
            aria-pressed={on}
            className="px-2.5 py-1 uppercase tracking-widest transition-colors"
            style={{
              background: on ? CHAMPAGNE  : "transparent",
              color:      on ? NAVY_DARK  : `${IVORY}CC`,
              fontWeight: on ? 700 : 400,
            }}>
            {code}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ── TopBar — Landing-Grade header used on every logged-in page ──────
 * Same DNA as `<LandingNav>` : navy_dark backdrop, champagne overline,
 * ivory typography, subtle blur.
 */
/**
 * ── TopBar — Landing-Grade header
 *
 * Since 2026-02-15, the TopBar is rendered ONCE by <AppShell> as a full-width
 * strip above the sidebar+main flex row. Pages that still call `<TopBar />`
 * inline get a no-op (harmless) so we can migrate progressively without
 * touching 60+ callers. Use `<AppTopBar>` (below) to render the actual header.
 */
export function TopBar() {
  // Legacy no-op — kept for backward compat with pages that still import it.
  return null;
}

// Actual header implementation — mounted by AppShell only.
export function AppTopBar({ title, subtitle, right }) {
  const { user } = useAuth();
  const roles = user?.roles || [];

  return (
    <header
      className="sticky top-0 z-40 border-b"
      style={{
        background: `${NAVY_DARK}E6`,
        backdropFilter: "blur(16px)",
        borderColor: `${CHAMPAGNE}1F`,
      }}
      data-testid="app-topbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-4
                          flex items-center gap-3 flex-wrap md:flex-nowrap">

        {/* Mobile hamburger — hidden ≥ md */}
        <MobileNav />

        {/* ── Brand + tagline (mirrors LandingNav) ─────────────── */}
        <Link to="/app" className="min-w-0 flex items-center gap-3 mr-auto group"
                  data-testid="topbar-brand">
          <span className="altiora-tree-hover hidden md:inline-flex">
            <AltioraTreeLogo size={32} />
          </span>
          <div className="leading-tight min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold"
                     style={{ color: CHAMPAGNE }}>
              ALTIORA · <span style={{ color: `${IVORY}AA` }}>
                Intelligence décisionnelle
              </span>
            </div>
            {title && (
              <h1 className="mt-0.5 font-serif text-lg md:text-xl font-medium
                                  tracking-tight truncate"
                      style={{ color: IVORY }}>
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-0.5 text-[11px] truncate"
                    style={{ color: `${IVORY}88` }}>
                {subtitle}
              </p>
            )}
          </div>
        </Link>

        {/* ── Right actions ────────────────────────────────────── */}
        <div className="flex items-center gap-2 shrink-0">
          {right}

          <div className="hidden sm:inline-flex">
            <CommandPaletteTrigger />
          </div>
          <a
            href="/securite"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="topbar-securite-ribbon"
            title="Sécurité & doctrine ALTIORA v2"
            aria-label="Sécurité & doctrine ALTIORA v2"
            className="hidden lg:inline-flex items-center gap-1.5 rounded-md px-2.5 py-1
                            transition hover:opacity-90"
            style={{ border: `1px solid ${CHAMPAGNE}33`,
                       color: `${IVORY}CC` }}>
            <ShieldCheck className="h-3 w-3" style={{ color: "#7EE0AA" }} />
            <span className="font-mono text-[10px] uppercase tracking-widest">
              Doctrine v2
            </span>
          </a>

          <div className="hidden md:inline-flex">
            <NotificationsBell />
          </div>

          <GamificationBadge />

          <div className="hidden sm:inline-flex">
            <LangSwitcher />
          </div>

          <div className="hidden md:inline-flex">
            <FounderProfileSwitcher />
          </div>

          <div
            className="hidden md:inline-flex items-center gap-1.5 rounded-md px-2.5 py-1"
            style={{ border: `1px solid ${CHAMPAGNE}33`,
                       background: `${NAVY}55` }}>
            <div className="h-1.5 w-1.5 rounded-full"
                 style={{ background: "#7EE0AA" }} />
            <span className="font-mono text-[10px] uppercase tracking-widest"
                     style={{ color: IVORY }}>
              {roles.includes("founder") ? "founder" : roles[0] || "user"}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}
